import { useState, useMemo, useCallback } from 'react';
import NudosGenericModal from '../../../NudosComponents/NudosGenericModal/NudosGenericModal';
import { XSign } from '../../../../assets/images/components/NudosComponents';
import NudosButton from '../../../NudosComponents/NudosButton/NudosButton';
import {
  CONFIG_CLASS_ID,
  IOrganizationConfig,
  IProductConfigurationCreate,
  IOrganizationConfigCreate,
  IProductConfiguration,
  CONFIG_SOURCE_REFERENCE_ID,
  IConfigDataItemCreate
} from '../../../../types/product.configuration.types';
import ConfigFieldsForm from './ConfigFieldsForm/ConfigFieldsForm';
import ConfigClassSelector from './ConfigClassSelector/ConfigClassSelector';
import SavedConfigClassSelector from './SavedConfigClassSelector/SavedConfigClassSelector';
import { CONFIG_CLASS } from '../../../../constants/configurations.constants';
import {
  addOrganizationConfig,
  addProductConfiguration
} from '../../../../services/suscription/configurations.service';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../utils/displayNudosStandardNotifications';

import './AddSettingsModal.scss';

interface IAddSettingsModal {
  productId: number;
  orderId: string;
  organizationId: number;
  productName?: string;
  configClassIds: CONFIG_CLASS_ID[];
  savedOrganizationConfigs: IOrganizationConfig[];
  isConfiguration?: boolean;
  closeModal: () => void;
  handleAddSettingsSubmit: () => void;
}

export const AddSettingsModal = ({
  productId,
  productName,
  orderId,
  organizationId,
  configClassIds,
  savedOrganizationConfigs,
  isConfiguration,
  closeModal,
  handleAddSettingsSubmit
}: IAddSettingsModal) => {
  const [selectedConfigClassId, setSelectedConfigClassId] = useState<CONFIG_CLASS_ID | null>(null);
  const [selectedConfig, setSelectedConfig] = useState<IOrganizationConfig | null>(null);
  const [selectedSavedClassName, setSelectedSavedClassName] = useState('');
  const [isNewConfigMode, setIsNewConfigMode] = useState(false);
  const [formValues, setFormValues] = useState<Record<number, string | File>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(true);

  const currentSavedConfigClasses = useMemo(() => {
    if (!selectedConfigClassId) return [];
    return savedOrganizationConfigs
      .filter(({ configClassId }) => configClassId === selectedConfigClassId)
      .filter(({ name }) => !!name);
  }, [selectedConfigClassId, savedOrganizationConfigs]);

  const handleSavedConfigSelection = useCallback(
    (configName: string, isNew: boolean) => {
      if (isNew) {
        setIsNewConfigMode(true);
        setSelectedConfig(null);
        setFormValues({});
      } else {
        setIsNewConfigMode(false);
        const config = currentSavedConfigClasses.find(c => c.name === configName);
        if (config) {
          setSelectedConfig(config);
          if (config.configData) {
            const autoFillValues: Record<number, string | File> = {};
            config.configData.forEach(dataItem => {
              autoFillValues[dataItem.fieldName.id] = dataItem.fieldValue;
            });
            setFormValues(autoFillValues);
          }
          setIsFormValid(true);
        }
      }
      setSelectedSavedClassName(configName);
    },
    [currentSavedConfigClasses]
  );

  const handleConfigClassSelection = useCallback((classId: CONFIG_CLASS_ID) => {
    setSelectedConfigClassId(classId);
    setSelectedConfig(null);
    setIsNewConfigMode(false);
    setSelectedSavedClassName('');
    setFormValues({});
    setIsFormValid(true);
  }, []);

  const createOrganizationConfig = useCallback(async (): Promise<IOrganizationConfig> => {
    const configData: IConfigDataItemCreate[] = [];
    let fileToUpload: File | null = null;
    Object.entries(formValues).forEach(([fieldId, value]) => {
      if (typeof value === 'string') {
        configData.push({
          fieldNameId: parseInt(fieldId),
          fieldValue: value
        });
      } else if (value instanceof File) {
        fileToUpload = value;
      }
    });
    const createOrganizationConfigData: IOrganizationConfigCreate = {
      organizationId,
      configClassId: selectedConfigClassId!,
      name: selectedSavedClassName,
      configData
    };
    if (fileToUpload) {
      createOrganizationConfigData.file = fileToUpload;
    }

    return await addOrganizationConfig(createOrganizationConfigData);
  }, [formValues, organizationId, selectedConfigClassId, selectedSavedClassName]);

  const createProductConfig = useCallback(
    async (organizationConfigId: number): Promise<IProductConfiguration> => {
      const dataToCreate: IProductConfigurationCreate = {
        organizationId,
        productId,
        organizationConfigId,
        sourceReferenceId: isConfiguration
          ? CONFIG_SOURCE_REFERENCE_ID.ORDER
          : CONFIG_SOURCE_REFERENCE_ID.LOGISTIC_SERVICE,
        sourceId: Number(orderId)
      };

      return await addProductConfiguration(dataToCreate);
    },
    [organizationId, productId, isConfiguration, orderId]
  );

  const handleSubmit = useCallback(async () => {
    try {
      setIsSubmitting(true);
      let organizationConfigId: number;
      if (isNewConfigMode) {
        const { id: newOrganizationConfigId } = await createOrganizationConfig();
        if (!newOrganizationConfigId) {
          throw new Error('Error al crear la configuración');
        }
        organizationConfigId = newOrganizationConfigId;
      } else {
        organizationConfigId = selectedConfig!.id;
      }
      await createProductConfig(organizationConfigId);
      displaySuccessNotification({
        customJSXMessage: <>Configuración agregada con éxito</>,
        duration: 3000
      });
      handleAddSettingsSubmit();
    } catch {
      displayErrorNotification();
    } finally {
      setIsSubmitting(false);
      closeModal();
    }
  }, [
    isNewConfigMode,
    createOrganizationConfig,
    createProductConfig,
    selectedConfig,
    handleAddSettingsSubmit,
    closeModal
  ]);

  const selectedConfigClass = selectedConfigClassId ? CONFIG_CLASS[selectedConfigClassId] : null;
  const showRequiredFieldsForm = selectedSavedClassName.length > 0 && selectedConfigClass?.fields !== undefined;

  const isButtonDisabled = useMemo(() => {
    if (!selectedConfigClassId || !selectedSavedClassName || isSubmitting) {
      return true;
    }
    if (isNewConfigMode && selectedSavedClassName.length > 0 && selectedConfigClass?.fields !== undefined) {
      return !isFormValid;
    }
    return false;
  }, [
    selectedConfigClassId,
    selectedSavedClassName,
    isSubmitting,
    isNewConfigMode,
    selectedConfigClass?.fields,
    isFormValid
  ]);

  return (
    <NudosGenericModal
      closeModalCallback={closeModal}
      modalContent={
        <div className="addSettingsModalContent">
          <XSign onClick={closeModal} className="closeModalButton" width={16} height={16} fill="#999999" />

          <header className="addSettingsModalHeader">
            <h1>Agregar configuraciones</h1>
            <h2>Estás por agregar configuraciones a la herramienta {productName}</h2>
          </header>

          <div className="addSettingsContentContainer customScrollbar">
            <div className="addSettingsContent">
              <ConfigClassSelector
                configClassIds={configClassIds}
                selectedConfigClassId={selectedConfigClassId}
                onClassSelection={handleConfigClassSelection}
              />

              {selectedConfigClass && (
                <SavedConfigClassSelector
                  selectedConfigClassName={selectedConfigClass.name}
                  currentSavedConfigClasses={currentSavedConfigClasses}
                  selectedSavedClassName={selectedSavedClassName}
                  onSavedClassSelection={handleSavedConfigSelection}
                />
              )}

              {showRequiredFieldsForm && (
                <ConfigFieldsForm
                  selectedConfigClass={selectedConfigClass}
                  isDisabled={!isNewConfigMode}
                  formValues={formValues}
                  setFormValues={setFormValues}
                  setIsFormValid={setIsFormValid}
                />
              )}
            </div>
          </div>
          <div className="addSettingsButtonContainer">
            <NudosButton
              buttonText="Agregar configuración"
              customHeight="38px"
              customWidth="180px"
              handleClick={handleSubmit}
              isButtonDisabled={isButtonDisabled}
            />
          </div>
        </div>
      }
    />
  );
};
